import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import CustomTable from "../../../components/custom-table/custom-table";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { CaseAppealsTableRowData } from "../../../data-model/caseAppealsTable";
import { FilterValues } from "../../../data-model/filterValues";
import { ModalId } from "../../../enums/modalId";
import { TableNames } from "../../../enums/tableNames";
import { useModalOpener } from "../../../hooks/useModalOpener";
import { setFilter } from "../../../redux/slice/filterValuesSlice";
import { setOpenedModalId } from "../../../redux/slice/modalSliceNew";
import { AppDispatch } from "../../../redux/store/store";
import { logError } from "../../../utils/customLogger";
import { useCaseAppealsTableData } from "../hooks/useCaseAppealsTableData";
import { useFilterAndSearch } from "../hooks/useFilterAndSearch";
import { useInitialDataFetchingForTable } from "../hooks/useInitialDataFetchingForTable";
import { useNextPageDataFetch } from "../hooks/useNextPageDataFetch";
import {
  CaseAppealsTableHeader,
  generateTableDataForCaseAppeals,
} from "../table/caseAppealsTableData";
import { createAllFilterSections } from "../utils/createFilterSections";
import { mapAppealRowToNonEpaModalData } from "../utils/mapAppealRowToNonEpaModalData";

/**
 * Case Appeals table used by both Astera and NYCBS pharma PA routes.
 */
const CaseAppealsOrdersTable = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { getTableDataFromContext, setTableDataForContext } =
    useTablesContext();

  useModalOpener();

  const pageSize = 50;

  // Filter options are static (the appeal list API only supports these three
  // fields), so they are built once and never depend on the loaded rows.
  const filterData = useMemo(() => createAllFilterSections(), []);

  const { caseAppealsState, setCaseAppealsState, tableData, setTableData } =
    useCaseAppealsTableData();

  const { initialDataFetchingExecuted, setInitialDataFetchingExecuted } =
    useInitialDataFetchingForTable(setCaseAppealsState, setTableData, pageSize);

  const { paginationResetCount, validFilters, searchTerm, setSearchTerm } =
    useFilterAndSearch(
      initialDataFetchingExecuted,
      setTableData,
      setCaseAppealsState,
      pageSize,
    );

  const { setEndIndex } = useNextPageDataFetch(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    caseAppealsState,
    setCaseAppealsState,
    setTableData,
    pageSize,
  );

  const appealsTableData = useMemo<CaseAppealsTableRowData[]>(
    () => generateTableDataForCaseAppeals(tableData),
    [tableData],
  );

  const handleRefresh = () => {
    setInitialDataFetchingExecuted(false);
  };

  /**
   * Action column Add/Edit: opens the Appeal Summary modal, the Case Appeals
   * copy of the calling-worklist Non-EPA Edit Summary modal.
   *
   * @param rowData - Formatted Case Appeals table row
   */
  const handleActionButtonClick = useCallback(
    (rowData: CaseAppealsTableRowData) => {
      dispatch(
        setOpenedModalId({
          id: ModalId.APPEAL_SUMMARY_MODAL,
          metaData: {
            data: mapAppealRowToNonEpaModalData(rowData),
          },
        }),
      );
    },
    [dispatch],
  );

  /**
   * History icon: opens the read-only appeal history side panel for the row.
   *
   * @param rowData - Formatted Case Appeals table row
   */
  const handleHistoryClick = useCallback(
    (rowData: CaseAppealsTableRowData) => {
      dispatch(
        setOpenedModalId({
          id: ModalId.APPEAL_HISTORY_PANEL,
          metaData: {
            data: mapAppealRowToNonEpaModalData(rowData),
          },
        }),
      );
    },
    [dispatch],
  );

  const handleFilterApply = (data: FilterValues) => {
    dispatch(setFilter(data));
  };

  const tableContextData = getTableDataFromContext(
    TableNames.ONCOLOGY_CASE_APPEALS_ORDERS,
  );

  useEffect(() => {
    if (tableContextData?.shouldRefetch) {
      setInitialDataFetchingExecuted(false);
      setTableDataForContext(TableNames.ONCOLOGY_CASE_APPEALS_ORDERS, {
        shouldRefetch: false,
      });
    }
  }, [
    tableContextData?.shouldRefetch,
    setInitialDataFetchingExecuted,
    setTableDataForContext,
  ]);

  useEffect(() => {
    if (caseAppealsState.error) {
      logError(
        new Error(caseAppealsState.error),
        "Error fetching appeal list data",
      );
      toast.error(caseAppealsState.error);
    }
  }, [caseAppealsState.error]);

  return (
    <CustomTable
      tableHeaders={CaseAppealsTableHeader}
      tableData={appealsTableData}
      itemsPerPage={10}
      pagesPerView={4}
      count={appealsTableData.length}
      totalCount={caseAppealsState.data?.total_count ?? 0}
      isFetching={caseAppealsState.loading}
      tableName={TableNames.ONCOLOGY_CASE_APPEALS_ORDERS}
      onRefreshButton={handleRefresh}
      isDisabled={caseAppealsState.loading}
      showRefreshButton={true}
      showFilterButton={true}
      hideSearchBar={false}
      searchingText={setSearchTerm}
      placeholder="Search by Patient Name, MRN and Rx Number"
      filterSectionsData={filterData}
      onFilterApplyClicked={handleFilterApply}
      endIndexOfTable={setEndIndex}
      requestForPaginationReset={paginationResetCount}
      showInLineLoader={caseAppealsState.showInlineLoader}
      onAddKeyButtonClick={handleActionButtonClick}
      onCallingHistoryClick={handleHistoryClick}
    />
  );
};

export default CaseAppealsOrdersTable;
